import dayjs, { Dayjs } from 'dayjs';
import round from 'lodash/round';
import pluralize from 'pluralize';
import { Matcher, TZDate } from 'react-day-picker';

import { FilterPreset, DatePeriod, DatePeriodInTimestamp } from './types';

export const buildPresetRange = ({
  preset,
  timezone,
  allowTimeInput = false,
  constraints = {},
}: {
  preset: FilterPreset;
  timezone: string;
  allowTimeInput?: boolean;
  constraints?: {
    minDate?: string;
    maxDate?: string;
  };
}): DatePeriod => {
  let { from, to } = preset.date(timezone, allowTimeInput);

  if (constraints?.minDate && dayjs(from).isBefore(constraints.minDate)) {
    from = constraints.minDate;
  }

  if (constraints?.maxDate && dayjs(to).isAfter(constraints.maxDate)) {
    to = constraints.maxDate;
  }

  return {
    from: new TZDate(from, timezone),
    to: new TZDate(to, timezone),
  };
};

export const isSelectedFilterPreset = ({
  value,
  filter,
  timezone,
  allowTimeInput = false,
  constraints = {},
}: {
  value: DatePeriod;
  filter: FilterPreset;
  timezone: string;
  allowTimeInput?: boolean;
  constraints?: {
    minDate?: string;
    maxDate?: string;
  };
}): boolean => {
  const presetRange = buildPresetRange({
    preset: filter,
    timezone,
    allowTimeInput,
    constraints,
  });
  return (
    !!value?.from &&
    Math.abs(dayjs(value.from).diff(presetRange.from, 'minutes')) < 5 &&
    !!value?.to &&
    Math.abs(dayjs(value.to).diff(presetRange.to, 'minutes')) < 5
  );
};

/** Takes a UTC date string (either ISO 8601 or RFC 2822) and returns a date-fns-tz TZDate object. */
export const utcToDate = (timestamp: string, timezone: string): TZDate => {
  return new TZDate(timestamp, timezone);
};

/** Takes a date-fns-tz TZDate object and returns a UTC date string in ISO format. */
export const dateToUtc = (date: TZDate): string => {
  return new Date(date.getTime()).toISOString();
};

export const utcToRange = (
  range: { from?: string; to?: string } | undefined,
  timezone: string
): DatePeriod => {
  return {
    from: range?.from ? new TZDate(range.from, timezone) : undefined,
    to: range?.to ? new TZDate(range.to, timezone) : undefined,
  };
};

export const rangeToUtc = ({ from, to }: DatePeriod): DatePeriodInTimestamp => {
  return {
    from: from ? dateToUtc(from) : undefined,
    to: to ? dateToUtc(to) : undefined,
  };
};

export const convertDate = (
  date: TZDate | undefined,
  oldTimezone: string,
  newTimezone: string
): TZDate | undefined => {
  /*
    dayjs.tz does not convert in between +00:00 timezones correctly...
    github issue: https://github.com/iamkun/dayjs/issues/1827#issuecomment-2479062677
  */
  const keepLocalTime =
    dayjs().tz(oldTimezone).format('Z') !== '+00:00' ||
    dayjs().tz(newTimezone).format('Z') !== '+00:00';

  return date
    ? new TZDate(
        dayjs.tz(date, oldTimezone).tz(newTimezone, keepLocalTime).toDate(),
        newTimezone
      )
    : undefined;
};

export const convertRange = (
  range: DatePeriod,
  oldTimezone: string,
  newTimezone: string
): DatePeriod => {
  return {
    from: convertDate(range.from, oldTimezone, newTimezone),
    to: convertDate(range.to, oldTimezone, newTimezone),
  };
};

export const utcNow = (): string => dayjs.utc().toISOString();

export const inputStringToDayjs = (
  dateInput: string,
  format: string,
  timezone: string
): Dayjs => dayjs(dateInput, format).tz(timezone, true);

export const dayjsToTZDate = (date: Dayjs, timezone: string): TZDate =>
  new TZDate(date.toDate(), timezone);

export const dateToInputString = (
  date: TZDate,
  format: string,
  timezone: string
): string => dayjs.tz(date, timezone).format(format);

type BuildDisabledMatcherArgs = {
  minDate?: string;
  maxDate?: string;
  timezone: string;
  fullDisabling?: boolean;
  maxNumberOfDays?: number;
  selected?: DatePeriod;
};

export const buildDisabledMatcher = ({
  minDate,
  maxDate,
  timezone,
  fullDisabling,
  maxNumberOfDays = 0,
  selected,
}: BuildDisabledMatcherArgs): Matcher | undefined => {
  if (fullDisabling) {
    return true;
  }

  // Disable the dates which are outside of selected date and maxNumberOfDays if user has selected only the "from" date
  if (selected?.from && !selected?.to && maxNumberOfDays > 0) {
    let before = dayjs(selected.from).subtract(maxNumberOfDays - 1, 'days');
    let after = dayjs(selected.from).add(maxNumberOfDays - 1, 'days');

    // Prevent selection of dates before minDate
    if (minDate) {
      const minDateTZ = dayjs.tz(minDate, timezone);

      if (minDateTZ.isAfter(before)) {
        before = minDateTZ;
      }
    }

    // Prevent selection of dates after maxDate
    if (maxDate) {
      const maxDateTZ = dayjs.tz(maxDate, timezone);

      if (maxDateTZ.isBefore(after)) {
        after = maxDateTZ;
      }
    }

    return {
      before: new TZDate(before.toISOString(), timezone),
      after: new TZDate(after.toISOString(), timezone),
    };
  }

  if (minDate && maxDate) {
    return {
      before: new TZDate(minDate, timezone),
      after: new TZDate(maxDate, timezone),
    };
  }

  if (maxDate) {
    return { after: new TZDate(maxDate, timezone) };
  }

  if (minDate) {
    return { before: new TZDate(minDate, timezone) };
  }
  return undefined;
};

export const periodToString = (activePeriod: number): string => {
  if (activePeriod < 60) {
    return `${activePeriod} ${pluralize('minute', activePeriod)}`;
  }

  if (activePeriod < 60 * 24) {
    const period = round(activePeriod / 60, 0);
    return `${period} ${pluralize('hour', period)}`;
  }

  const period = round(activePeriod / 60 / 24, 0);
  return `${period} ${pluralize('day', period)}`;
};

export const shouldHideHoverTrail = (
  selectedRange?: DatePeriod,
  hoveredDay?: TZDate,
  maxNumberOfDays?: number
): boolean => {
  if (
    !selectedRange?.from ||
    selectedRange?.to ||
    !hoveredDay ||
    dayjs(selectedRange.from).isSame(hoveredDay)
  ) {
    return true;
  }

  // hide trail if range is after max number of days limit
  if (maxNumberOfDays) {
    const hovered = dayjs(hoveredDay);
    const fromDate = dayjs(selectedRange.from);

    if (
      hovered.isAfter(fromDate) &&
      hovered.isAfter(fromDate.add(maxNumberOfDays - 1, 'day'))
    ) {
      return true;
    }

    if (
      hovered.isBefore(fromDate) &&
      hovered.isBefore(fromDate.subtract(maxNumberOfDays - 1, 'day'))
    ) {
      return true;
    }
  }

  return false;
};
